import type { ContentPage } from "../types";

// Jammable numbers are the ones in how-to-train-an-ai-voice-model.ts
// (lib/train/clips.ts, lib/train/eta.ts); text-to-speech is a stock voice
// re-sung by the RVC model (createTts in pages/api/v2/conversion). Competitor
// facts are from each tool's own pages on the `verified` date, listed in
// `sources`. Re-check them when that date is more than 90 days old.
const page: ContentPage = {
  section: "blog",
  slug: "ai-voice-cloning",
  coverLabel: "Voice cloning",
  title: "AI Voice Cloning: How to Clone Your Own Voice (2026)",
  metaDescription:
    "What AI voice cloning is, speech cloning vs singing voice conversion, how to clone your own voice, the audio you need, and voice cloning software compared.",
  h1: "AI voice cloning: how to clone your own voice",
  kind: "Guide",
  stats: [
    { value: "3 min", label: "of clean audio to clone your voice on Jammable" },
    { value: "~25 min", label: "to train a voice on 10 minutes of audio" },
    { value: "5", label: "voice cloning tools compared" },
  ],
  answer:
    "AI voice cloning trains a model on recordings of one voice so a computer can speak or sing in it. Speech cloning, like ElevenLabs, reads text aloud; singing voice conversion, like the RVC models Jammable trains, re-sings a vocal. On Jammable, 3 minutes of your own voice is enough, and a 10-minute dataset is usually ready in about 25 minutes.",
  takeaways: [
    "There are two kinds of voice cloning: speech cloning for text-to-speech and singing voice conversion for vocals.",
    "Clean audio of one voice matters more than length: 6 to 10 minutes is a good target on Jammable.",
    "ElevenLabs is the stronger pick for spoken voices; Jammable, Kits.ai and RVC are built for singing.",
    "Clone your own voice, or a voice whose owner has agreed to it.",
  ],
  queries: [
    "ai voice cloning",
    "voice cloning software",
    "clone my voice",
    "best ai voice cloning",
    "how to clone your voice",
    "how to make an ai voice of yourself",
  ],
  published: "2026-10-17",
  updated: "2026-10-17",
  verified: "2026-10-17",
  blocks: [
    {
      type: "md",
      text: `## What is AI voice cloning?

AI voice cloning means training a machine learning model on recordings of one person's voice until it can reproduce that voice's tone, accent and texture. Once trained, the model can make new audio in that voice: a sentence it never heard, or a melody it was never given.

The model learns what a voice sounds like, not what it says. That is why the recordings need to be clean and contain only that voice: anything else in the audio, from a second speaker to room echo, gets learned as part of the voice.

## The two kinds of AI voice cloning

"Voice cloning" covers two different technologies, and most tools are built for one of them.

- **Speech cloning (text-to-speech).** You type text and the model reads it out in the cloned voice. It is used for narration, videos, podcasts and apps. ElevenLabs is the best-known example.
- **Singing voice conversion.** You give the model a vocal and it re-sings it in the cloned voice, keeping the melody, timing and words of the original. The most common open model for this is RVC (Retrieval-based Voice Conversion), which is what Jammable trains. See [what an RVC model is](/blog/what-is-rvc) for how it works.

The difference matters when you pick a tool. A speech clone does not sing, and ElevenLabs says its Professional Voice Clones do not currently support singing. A singing model can speak, but it needs a spoken input to convert: on Jammable, text-to-speech reads your text with a stock voice (you pick the gender, accent and expression) and your model then re-voices it.`,
    },
    {
      type: "table",
      heading: "Speech cloning vs singing voice conversion",
      table: {
        columns: ["", "Speech cloning", "Singing voice conversion (RVC)"],
        rows: [
          ["You give it", "Text", "A vocal recording"],
          [
            "You get",
            "Speech in the cloned voice",
            "The same vocal, re-sung in the cloned voice",
          ],
          [
            "Good for",
            "Narration, voiceovers, apps",
            "Covers, demos, harmonies, duets",
          ],
          [
            "Melody and timing",
            "Made up by the model",
            "Taken from the input vocal",
          ],
          [
            "Examples",
            "ElevenLabs, Kits.ai speaking voices",
            "Jammable, Kits.ai, RVC WebUI, Applio",
          ],
        ],
      },
    },
    {
      type: "callout",
      tone: "note",
      title: "Whose voice you can clone",
      text: "Clone your own voice, or the voice of someone who has agreed to it. ElevenLabs asks you to confirm you have the right and consent before it saves an instant clone, and only lets you make a Professional Voice Clone of your own voice.",
    },
    {
      type: "md",
      text: `## What audio you need to clone your voice

The recordings decide how good the clone is. For a singing voice on Jammable:

- **At least 3 minutes of voice**, up to 60 minutes. Silence is cut out automatically, so only singing or speech counts. 6 to 10 minutes usually sounds noticeably better than the minimum.
- **One voice only.** No backing vocals, no second speaker, no crowd.
- **No music behind it if you can help it.** Jammable can remove instrumentals, but a dry recording is always cleaner.
- **A quiet room without echo.** Reverb and background noise get baked into the voice.
- **Range.** Sing your low and high notes, soft and loud. A model trained only on quiet speech struggles with a loud chorus.
- **MP3, WAV or M4A files**, or record straight into the page with your microphone.

Other tools ask for different amounts. ElevenLabs recommends 1 to 2 minutes of clear speech for an instant clone and says more than 3 minutes adds little; its Professional Voice Cloning needs at least 30 minutes and recommends 2 to 3 hours. Kits.ai recommends 10 to 60 minutes of dry, one-note-at-a-time vocals. The RVC project says a good model can be trained on 10 minutes or less.`,
    },
    {
      type: "steps",
      heading: "How to clone your own voice on Jammable",
      items: [
        {
          title: "Record or upload your voice",
          body: "Open [Train](/train) and upload MP3, WAV or M4A files, or press Record and sing or talk into your microphone. Aim for 6 to 10 minutes after silence is removed.",
        },
        {
          title: "Trim what isn't you",
          body: "Each file shows how much voice is left. Cut coughs, a second person or a noisy intro.",
        },
        {
          title: "Name it and pick who can use it",
          body: "Add a name and picture, then choose *Private, only me* or *Public in the library*.",
        },
        {
          title: "Clean up if needed and start training",
          body: "Turn on *Remove instrumentals* or *Remove reverb* only if the recordings need it, then start. Training runs on Jammable's servers and you get an email when it's done.",
        },
        {
          title: "Use your AI voice",
          body: "The voice appears in your [Library](/library). Pick it on the [cover maker](/create) to sing, or use it for text-to-speech.",
        },
      ],
    },
    {
      type: "md",
      text: `This is the short version. The full walkthrough, with recording tips and a table of training times by dataset size, is in [how to train your own AI voice model](/blog/how-to-train-an-ai-voice-model).

Training is part of the Creator plan ($9.99 for the first month, then $24.99 a month, with unlimited training) and the Power plan, and training passes can be bought on their own. If you already trained an RVC model on your own computer, you can upload the .pth and .index files instead of training again.

## How long does voice cloning take?

On Jammable, a voice trained on up to 10 minutes of audio is usually ready in about 25 minutes, and one trained on a full hour in about 1 hour 45 minutes. You can close the page while it runs. ElevenLabs' professional clones take hours to fine-tune: its own pages give estimates from 3 to 6 hours up to 24 hours. Kits.ai says its training takes from 30 minutes to several hours depending on the size of the dataset.

## How good does an AI voice clone get?

With clean, varied audio, a singing model reproduces the tone and texture of a voice closely. What it can't do is invent skills the voice never showed it:

- **It copies your recordings, flaws included.** Hiss, echo, autotune and heavy compression all come back in the output.
- **Range is learned, not guessed.** If you never sang high notes in the dataset, high notes in a cover will sound strained. Shift the pitch, or record more range and retrain.
- **The input vocal sets the performance.** Conversion keeps the timing, phrasing and emotion of the vocal you feed it. A flat input gives a flat result in any voice.
- **Speech and singing are separate skills.** A model trained only on speech will sing, but less convincingly than one trained on singing, and the reverse.
- **No live mode on Jammable.** Jammable works on recordings in the browser on any device; it does not change your voice live on calls or streams. The RVC WebUI includes a real-time mode if you run it on your own computer.

If a clone sounds thin or robotic, the fix is almost always better data: add more varied, cleaner audio and train again.`,
    },
    {
      type: "matrix",
      heading: "Voice cloning software compared",
      matrix: {
        caption:
          "Checked against each tool's own pages on 2026-10-17. Monthly prices in USD.",
        tools: [
          "Jammable",
          "ElevenLabs",
          "Kits.ai",
          "ACE Studio",
          "RVC / Applio",
        ],
        rows: [
          {
            feature: "Clones a singing voice",
            values: ["yes", "no", "yes", "yes", "yes"],
          },
          {
            feature: "Clones a voice for text-to-speech",
            values: ["partial", "yes", "yes", "Not listed", "partial"],
          },
          {
            feature: "Audio suggested to train",
            values: [
              "3 to 60 min (6+ better)",
              "1 to 2 min instant, 30 min+ professional",
              "10 to 60 min",
              "Not stated",
              "About 10 min",
            ],
          },
          {
            feature: "Where it runs",
            values: [
              "Browser, any device",
              "Browser",
              "Browser",
              "Desktop app",
              "Your own computer",
            ],
          },
          {
            feature: "Other voices to use",
            values: [
              "60,000+ community voices",
              "10,000+ voices in its library",
              "100+ licensed voices",
              "140+ singing voices",
              "Bring your own models",
            ],
          },
          {
            feature: "Cloning from",
            values: [
              "Creator: $9.99 first month, then $24.99/mo",
              "$6/mo instant, $22/mo professional",
              "$10/mo, 2 voice slots",
              "Paid plans, 1 to 5 clone slots",
              "Free, open source",
            ],
          },
        ],
      },
    },
    {
      type: "md",
      text: `## The best AI voice cloning tools

- **Jammable** is built for singing in your own voice. Train from 3 minutes of audio in the browser, then use the voice for covers, duets with up to 6 voices and text-to-speech, alongside 60,000+ community voices. Every plan has unlimited covers. Its speech is a stock voice re-sung by your model, so it is not the best choice for long narration. See [Jammable vs ElevenLabs](/compare/jammable-vs-elevenlabs).
- **ElevenLabs** is the stronger tool for spoken voices. Instant clones need only 1 to 2 minutes of speech and start on the $6 Starter plan; Professional Voice Cloning (Creator, $22) trains on 30 minutes to 3 hours and verifies the voice is yours. It does not clone singing.
- **Kits.ai** clones singing voices and adds producer tools such as stem splitting, harmonies and a choir tool. Its library voices are licensed from the singers themselves, which suits commercial releases. Starter costs $10 a month with 2 voice slots; Producer ($30) removes the slot limit. See [Jammable vs Kits.ai](/compare/jammable-vs-kits-ai).
- **ACE Studio** is a desktop music workspace whose Vocal Synth turns MIDI notes and lyrics into singing. You can train your own singing voice from vocal samples, with 1 clone slot on its lower paid plan and 5 on the higher one.
- **RVC WebUI and Applio** are free, MIT-licensed tools for training and running RVC models on your own computer. They need a capable machine and some setup. Applio's maintainers say it will no longer get frequent updates.`,
    },
    {
      type: "verdicts",
      heading: "Which voice cloning software should you use?",
      items: [
        {
          useCase: "Sing covers in your own voice",
          pick: "Jammable",
          why: "Train from 3 minutes of audio in the browser, then make unlimited covers with it.",
        },
        {
          useCase: "Narration and voiceovers",
          pick: "ElevenLabs",
          why: "Its speech clones read text directly and it offers a verified professional clone of your own voice.",
        },
        {
          useCase: "Licensed voices for a release",
          pick: "Kits.ai",
          why: "Its library voices are licensed from the singers, and it bundles stems and mastering.",
        },
        {
          useCase: "Writing vocal lines from notes",
          pick: "ACE Studio",
          why: "It sings MIDI and lyrics you write, so you control every note.",
        },
        {
          useCase: "Free, on your own computer",
          pick: "RVC / Applio",
          why: "Open source and free, if you have the hardware and don't mind setup.",
        },
      ],
    },
    {
      type: "cta",
      href: "/train",
      label: "Clone your voice",
      text: "Add a few minutes of your own voice and Jammable trains the model for you.",
    },
  ],
  faqs: [
    {
      q: "How do I clone my own voice?",
      a: "Record at least 3 minutes of clean singing or speech in a quiet room, ideally 6 to 10 minutes, and upload it to a voice cloning tool. On Jammable you do this on the Train page; a 10-minute dataset is usually ready in about 25 minutes and the voice then appears in your library.",
    },
    {
      q: "What is the best AI voice cloning software?",
      a: "It depends on what the voice is for. For singing, Jammable trains an RVC model from 3 minutes of audio in the browser. For spoken voices, ElevenLabs is stronger. Kits.ai suits producers who want licensed voices, and RVC WebUI or Applio are free if you run them on your own computer.",
    },
    {
      q: "Is AI voice cloning free?",
      a: "The open-source RVC WebUI and Applio are free but run on your own computer. ElevenLabs' free plan does not include cloning; instant clones start at $6 a month. On Jammable, voice training comes with the Creator and Power plans or a training pass.",
    },
    {
      q: "Can I clone someone else's voice?",
      a: "Only with their agreement. Use your own voice, or the voice of someone who has said yes. ElevenLabs asks you to confirm consent before saving a clone and only allows Professional Voice Clones of your own voice.",
    },
    {
      q: "Can an AI voice clone sing?",
      a: "A singing voice conversion model can. Jammable, Kits.ai, ACE Studio and RVC all clone singing voices. Speech cloning tools generally can't: ElevenLabs says its Professional Voice Clones do not currently support singing.",
    },
    {
      q: "Can I use my cloned voice live?",
      a: "Not on Jammable, which works on recordings rather than live audio. The RVC WebUI has a real-time mode if you run it on your own computer.",
    },
  ],
  related: [
    {
      label: "How to train your own AI voice model",
      href: "/blog/how-to-train-an-ai-voice-model",
    },
    { label: "What is an RVC model?", href: "/blog/what-is-rvc" },
    {
      label: "AI singing voice generator",
      href: "/blog/ai-singing-voice-generator",
    },
    {
      label: "Jammable vs ElevenLabs",
      href: "/compare/jammable-vs-elevenlabs",
    },
    { label: "Train your voice", href: "/train" },
  ],
  sources: [
    {
      label: "ElevenLabs voice cloning",
      href: "https://elevenlabs.io/voice-cloning",
    },
    { label: "ElevenLabs pricing", href: "https://elevenlabs.io/pricing" },
    {
      label: "ElevenLabs Voice Library",
      href: "https://elevenlabs.io/voice-library",
    },
    {
      label: "ElevenLabs instant voice cloning docs",
      href: "https://elevenlabs.io/docs/product-guides/voices/voice-cloning/instant-voice-cloning",
    },
    {
      label: "ElevenLabs professional voice cloning docs",
      href: "https://elevenlabs.io/docs/product-guides/voices/voice-cloning/professional-voice-cloning",
    },
    { label: "Kits.ai", href: "https://www.kits.ai/" },
    { label: "Kits.ai pricing", href: "https://www.kits.ai/pricing" },
    {
      label: "Kits.ai: What is Professional Voice Cloning?",
      href: "https://help.kits.ai/hc/en-us/articles/25367001343635",
    },
    { label: "ACE Studio", href: "https://acestudio.ai/" },
    { label: "ACE Studio pricing", href: "https://acestudio.ai/pricing" },
    {
      label: "RVC WebUI on GitHub",
      href: "https://github.com/RVC-Project/Retrieval-based-Voice-Conversion-WebUI",
    },
    { label: "Applio on GitHub", href: "https://github.com/IAHispano/Applio" },
  ],
};

export default page;
